/**
 * NiV "נתונים" tab — morning business dashboard over the clinics overview +
 * prospects board. Built to be shown on an office screen: auto-refreshes
 * every 5 minutes.
 *
 * Sections:
 * 1. Header — Hebrew date + last-refresh time
 * 2. Hero KPIs — MRR, paying clinics, ARPU, annual revenue before VAT
 * 3. Secondary KPIs — leads/new clinics this month, trials, patients, therapists…
 * 4. Attention lists — billing ending this month, payment queue, trials ending soon
 * 5. Charts — leads vs registrations, payments by month, clinics per plan,
 *    activity health, billing-cycle & revenue-by-plan breakdowns
 *
 * All money figures derive from the manual CRM layer (niv_clinic_crm).
 * Pre-VAT figures use VAT_RATE from shared/nivDerived.js.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell,
} from "recharts";
import {
  useGetNivClinicsOverviewQuery,
  useGetProspectsBoardQuery,
} from "../../../../store/services/nivApi";
import {
  formatShekels,
  formatDate,
  normalizeMonthlyAmount,
  crmAmountPreVat,
  isPayingRow,
  getActivityStatus,
  getTrialDaysLeft,
  getPlanEndStatus,
  buildPaymentsQueue,
  BILLING_CYCLE_LABELS,
  VAT_PERCENT_LABEL,
} from "../shared/nivDerived";
import {
  Wallet,
  ChartLineUp,
  UsersThree,
  Coins,
  Timer,
  UserPlus,
  Sparkle,
  HeartStraight,
  Stethoscope,
  ArrowsClockwise,
  Warning,
  CalendarBlank,
  ArrowClockwise,
} from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import "./NivDataTab.css";

// Chart colors. Leads-blue is defined as --ndt-leads in NivDataTab.css;
// the pair (blue + primary-dark teal) passes CVD/contrast validation with
// legend + tooltip as relief.
const COLOR_MAIN = "var(--color-primary-dark)";
const COLOR_LEADS = "var(--ndt-leads)";
const MONTHS_BACK = 12;
const REFRESH_MS = 5 * 60 * 1000;

const monthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const monthLabel = (key) => {
  const [year, month] = key.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("he-IL", {
    month: "short",
    year: "2-digit",
  });
};

const lastMonthKeys = (count) => {
  const now = new Date();
  const keys = [];
  for (let i = count - 1; i >= 0; i--) {
    keys.push(monthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
  }
  return keys;
};

const isInCurrentMonth = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return false;
  return monthKey(date) === monthKey(new Date());
};

const ChartTooltip = ({ active, payload, label, isMoney }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ndt-tooltip">
      <div className="ndt-tooltip-label">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="ndt-tooltip-row">
          <span className="ndt-tooltip-swatch" style={{ background: entry.fill || entry.color }} />
          <span>{entry.name}:</span>
          <strong>{isMoney ? formatShekels(entry.value) : entry.value}</strong>
        </div>
      ))}
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, hero, tone }) => (
  <div className={`ndt-kpi${hero ? " ndt-kpi-hero" : ""}${tone ? ` ndt-kpi-${tone}` : ""}`}>
    <div className="ndt-kpi-icon"><Icon size={18} /></div>
    <span className="ndt-kpi-label">{label}</span>
    <span className="ndt-kpi-value">{value}</span>
    {sub && <span className="ndt-kpi-sub">{sub}</span>}
  </div>
);

function NivDataTab({ isAuthorized }) {
  const navigate = useNavigate();
  const { data: allRows = [], isLoading, fulfilledTimeStamp } = useGetNivClinicsOverviewQuery(
    undefined,
    { skip: !isAuthorized, pollingInterval: REFRESH_MS }
  );
  // Admin-deactivated clinics are excluded from all analytics
  const rows = useMemo(() => allRows.filter((r) => !r.inactiveByAdmin), [allRows]);
  const { data: board } = useGetProspectsBoardQuery(undefined, {
    skip: !isAuthorized,
    pollingInterval: REFRESH_MS,
  });
  const prospects = useMemo(() => board?.prospects || [], [board]);

  const stats = useMemo(() => {
    const paying = rows.filter(isPayingRow);
    // MRR before VAT: monthly deals + one-time deals spread over their window
    const mrr = paying.reduce((sum, r) => sum + normalizeMonthlyAmount(r.crm, r.planEndDate), 0);
    const leftCount = rows.filter((r) => r.crm?.paymentStatus === "left").length;

    // --- Secondary KPIs ---
    const trials = rows.filter((r) => r.plan === "trial");
    const trialsActive = trials.filter((r) => (getTrialDaysLeft(r.trialEndDate) ?? -1) >= 0);
    const trialsEndingSoon = trialsActive
      .map((r) => ({ ...r, daysLeft: getTrialDaysLeft(r.trialEndDate) }))
      .filter((r) => r.daysLeft <= 7)
      .sort((a, b) => a.daysLeft - b.daysLeft);
    const newClinicsThisMonth = rows.filter((r) => isInCurrentMonth(r.createdAt));
    const leadsThisMonth = prospects.filter((p) => isInCurrentMonth(p.createdAt));
    const activePatientsTotal = rows.reduce((s, r) => s + (r.activePatients || 0), 0);
    const therapistsTotal = rows.reduce((s, r) => s + (r.therapistsCount || 0), 0);
    const standingOrderCount = paying.filter((r) => r.crm?.isStandingOrder).length;

    // --- Billing ending this calendar month ---
    const billingEndingThisMonth = rows
      .filter((r) => r.planEndDate && isInCurrentMonth(r.planEndDate))
      .map((r) => ({ ...r, endStatus: getPlanEndStatus(r.planEndDate) }))
      .sort((a, b) => new Date(a.planEndDate) - new Date(b.planEndDate));

    // --- Payment attention queue (expired / soon / paying without end date) ---
    const paymentsQueue = buildPaymentsQueue(rows);

    // --- Clinics per plan ---
    const planCountMap = {};
    rows.forEach((r) => {
      const label = r.planName || r.plan || "לא ידוע";
      planCountMap[label] = (planCountMap[label] || 0) + 1;
    });
    const planData = Object.entries(planCountMap)
      .map(([plan, count]) => ({ plan, count }))
      .sort((a, b) => b.count - a.count);

    // --- Revenue (MRR) per plan, paying clinics only ---
    const planRevenueMap = {};
    paying.forEach((r) => {
      const label = r.planName || r.plan || "לא ידוע";
      planRevenueMap[label] = (planRevenueMap[label] || 0) + normalizeMonthlyAmount(r.crm, r.planEndDate);
    });
    const planRevenue = Object.entries(planRevenueMap)
      .map(([plan, amount]) => ({ plan, amount }))
      .sort((a, b) => b.amount - a.amount);
    const planRevenueMax = planRevenue[0]?.amount || 1;

    // --- Billing-cycle breakdown of paying clinics ---
    const cycleBreakdown = ["monthly", "one_time"].map((cycle) => {
      const group = paying.filter((r) => (r.crm?.billingCycle || "monthly") === cycle);
      return {
        cycle,
        label: BILLING_CYCLE_LABELS[cycle],
        count: group.length,
        mrr: group.reduce((s, r) => s + normalizeMonthlyAmount(r.crm, r.planEndDate), 0),
      };
    }).filter((c) => c.count > 0);

    // --- Monthly series ---
    const keys = lastMonthKeys(MONTHS_BACK);
    const paymentSums = {};
    rows.forEach((r) => {
      (r.crm?.payments || []).forEach((p) => {
        const date = new Date(p.date);
        if (Number.isNaN(date.getTime())) return;
        const key = monthKey(date);
        // Pre-VAT (dashboard convention); legacy records fall back to the CRM VAT flag
        paymentSums[key] =
          (paymentSums[key] || 0) +
          (p.amountBeforeVat != null ? Number(p.amountBeforeVat) || 0 : crmAmountPreVat({ ...r.crm, amount: p.amount, amountBeforeVat: null }));
      });
    });
    const monthsData = keys.map((key) => ({
      month: monthLabel(key),
      total: Math.round(paymentSums[key] || 0),
    }));

    const leadSums = {};
    prospects.forEach((p) => {
      const date = new Date(p.createdAt);
      if (Number.isNaN(date.getTime())) return;
      const key = monthKey(date);
      leadSums[key] = (leadSums[key] || 0) + 1;
    });
    const registrationSums = {};
    rows.forEach((r) => {
      const date = new Date(r.createdAt);
      if (Number.isNaN(date.getTime())) return;
      const key = monthKey(date);
      registrationSums[key] = (registrationSums[key] || 0) + 1;
    });
    const growthData = keys.map((key) => ({
      month: monthLabel(key),
      leads: leadSums[key] || 0,
      registered: registrationSums[key] || 0,
    }));

    // --- Activity health (last real activity on the clinic doc) ---
    const activityOrder = ["active", "recent", "week", "inactive", "gone", "unknown"];
    const activityMap = {};
    rows.forEach((r) => {
      const status = getActivityStatus(r.updatedAt);
      if (!activityMap[status.level]) activityMap[status.level] = { ...status, count: 0 };
      activityMap[status.level].count += 1;
    });
    const activityData = activityOrder
      .filter((level) => activityMap[level])
      .map((level) => ({
        label: activityMap[level].label,
        count: activityMap[level].count,
        color: activityMap[level].color,
      }));

    return {
      totalClinics: rows.length,
      payingCount: paying.length,
      mrr,
      arpu: paying.length ? mrr / paying.length : 0,
      annualPreVat: mrr * 12,
      leftCount,
      trialsActiveCount: trialsActive.length,
      trialsEndingSoon,
      newClinicsThisMonth: newClinicsThisMonth.length,
      leadsThisMonth: leadsThisMonth.length,
      activePatientsTotal,
      therapistsTotal,
      standingOrderCount,
      billingEndingThisMonth,
      paymentsQueue,
      planData,
      planRevenue,
      planRevenueMax,
      cycleBreakdown,
      monthsData,
      hasPayments: monthsData.some((m) => m.total > 0),
      growthData,
      hasGrowth: growthData.some((m) => m.leads > 0 || m.registered > 0),
      activityData,
    };
  }, [rows, prospects]);

  const goToClinic = (clinicId) => navigate(`/dashboard/niv/clinic/${clinicId}`);

  if (isLoading) {
    return (
      <ContentLoader message="טוען נתונים…" />
    );
  }

  return (
    <div className="ndt-container">
      {/* Header */}
      <div className="ndt-header">
        <div>
          <h2 className="ndt-title">תמונת מצב עסקית</h2>
          <span className="ndt-date">
            {new Date().toLocaleDateString("he-IL", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </div>
        <span className="ndt-refresh">
          <ArrowClockwise size={12} />
          מתעדכן כל 5 דקות
          {fulfilledTimeStamp &&
            ` · עודכן ${new Date(fulfilledTimeStamp).toLocaleTimeString("he-IL", {
              hour: "2-digit",
              minute: "2-digit",
            })}`}
        </span>
      </div>

      {/* Hero KPIs */}
      <div className="ndt-kpi-grid">
        <Kpi
          hero
          icon={ChartLineUp}
          label='הכנסה חודשית חוזרת (MRR) לפני מע"מ'
          value={formatShekels(stats.mrr)}
          sub="חודשי + חד-פעמי פרוס על חודשי העסקה"
        />
        <Kpi
          icon={UsersThree}
          label="לקוחות משלמים"
          value={stats.payingCount}
          sub={`מתוך ${stats.totalClinics} קליניקות`}
        />
        <Kpi
          icon={Wallet}
          label='תשלום חודשי ממוצע (ARPU) לפני מע"מ'
          value={formatShekels(stats.arpu)}
          sub="ממוצע ללקוח משלם"
        />
        <Kpi
          icon={Coins}
          label='הכנסה שנתית לפני מע"מ'
          value={formatShekels(stats.annualPreVat)}
          sub={`MRR × 12, לפני ${VAT_PERCENT_LABEL}`}
        />
      </div>

      {/* Secondary KPIs */}
      <div className="ndt-kpi-grid ndt-kpi-grid-small">
        <Kpi icon={Sparkle} label="לידים החודש" value={stats.leadsThisMonth} />
        <Kpi icon={UserPlus} label="נרשמו החודש" value={stats.newClinicsThisMonth} />
        <Kpi icon={Timer} label="בתקופת ניסיון" value={stats.trialsActiveCount} />
        <Kpi icon={HeartStraight} label="מטופלים פעילים" value={stats.activePatientsTotal.toLocaleString("he-IL")} />
        <Kpi icon={Stethoscope} label="מטפלים במערכת" value={stats.therapistsTotal} />
        <Kpi
          icon={ArrowsClockwise}
          label='הו"ק באשראי'
          value={stats.standingOrderCount}
          sub={stats.payingCount ? `מתוך ${stats.payingCount} משלמים · ${stats.leftCount} עזבו` : undefined}
        />
      </div>

      {/* Attention lists */}
      <div className="ndt-lists-grid">
        <section className="ndt-list-card">
          <h3 className="ndt-chart-title">
            <CalendarBlank size={15} />
            מסיימים תקופת חיוב החודש {stats.billingEndingThisMonth.length > 0 && `(${stats.billingEndingThisMonth.length})`}
          </h3>
          {stats.billingEndingThisMonth.length === 0 ? (
            <div className="ndt-empty ndt-empty-sm">אין קליניקות שמסיימות החודש</div>
          ) : (
            <div className="ndt-list">
              {stats.billingEndingThisMonth.map((r) => (
                <button key={r.clinicId} type="button" className="ndt-list-row" onClick={() => goToClinic(r.clinicId)}>
                  <span className="ndt-list-name">{r.name}</span>
                  <span className="ndt-list-meta">
                    {isPayingRow(r) ? formatShekels(crmAmountPreVat(r.crm)) : "—"}
                    {r.crm?.billingCycle && ` · ${BILLING_CYCLE_LABELS[r.crm.billingCycle]}`}
                  </span>
                  <span className={`ndt-list-date${r.endStatus?.level === "expired" ? " is-danger" : ""}`}>
                    {formatDate(r.planEndDate)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="ndt-list-card">
          <h3 className="ndt-chart-title">
            <Warning size={15} />
            תשלומים טעוני טיפול {stats.paymentsQueue.length > 0 && `(${stats.paymentsQueue.length})`}
          </h3>
          {stats.paymentsQueue.length === 0 ? (
            <div className="ndt-empty ndt-empty-sm">הכל מסודר — אין מה לטפל 🎉</div>
          ) : (
            <div className="ndt-list">
              {stats.paymentsQueue.map(({ row, kind, label }) => (
                <button key={row.clinicId} type="button" className="ndt-list-row" onClick={() => goToClinic(row.clinicId)}>
                  <span className="ndt-list-name">{row.name}</span>
                  <span className="ndt-list-meta">
                    {isPayingRow(row) ? formatShekels(crmAmountPreVat(row.crm)) : "—"}
                  </span>
                  <span className={`ndt-badge ${kind === "expired" ? "ndt-badge-danger" : "ndt-badge-warn"}`}>
                    {label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="ndt-list-card">
          <h3 className="ndt-chart-title">
            <Timer size={15} />
            ניסיון מסתיים בשבוע הקרוב {stats.trialsEndingSoon.length > 0 && `(${stats.trialsEndingSoon.length})`}
          </h3>
          {stats.trialsEndingSoon.length === 0 ? (
            <div className="ndt-empty ndt-empty-sm">אין ניסיונות שמסתיימים השבוע</div>
          ) : (
            <div className="ndt-list">
              {stats.trialsEndingSoon.map((r) => (
                <button key={r.clinicId} type="button" className="ndt-list-row" onClick={() => goToClinic(r.clinicId)}>
                  <span className="ndt-list-name">{r.name}</span>
                  <span className="ndt-list-meta">{r.activePatients} מטופלים פעילים</span>
                  <span className={`ndt-badge ${r.daysLeft <= 2 ? "ndt-badge-danger" : "ndt-badge-warn"}`}>
                    {r.daysLeft === 0 ? "היום" : `${r.daysLeft} ימים`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Charts */}
      <div className="ndt-charts-grid">
        <section className="ndt-chart-card">
          <h3 className="ndt-chart-title">לידים מול הרשמות לפי חודש</h3>
          <div className="ndt-legend">
            <span className="ndt-legend-item">
              <span className="ndt-legend-swatch" style={{ background: "var(--ndt-leads)" }} />
              לידים
            </span>
            <span className="ndt-legend-item">
              <span className="ndt-legend-swatch" style={{ background: "var(--color-primary-dark)" }} />
              נרשמו
            </span>
          </div>
          {!stats.hasGrowth ? (
            <div className="ndt-empty">אין נתונים להצגה</div>
          ) : (
            <div className="ndt-chart" dir="ltr">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.growthData} margin={{ top: 16, right: 8, left: 8, bottom: 0 }} barGap={2}>
                  <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                    interval={1}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: "var(--color-text-light)" }}
                    width={28}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
                  <Bar dataKey="leads" name="לידים" fill={COLOR_LEADS} maxBarSize={14} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="registered" name="נרשמו" fill={COLOR_MAIN} maxBarSize={14} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="ndt-chart-card">
          <h3 className="ndt-chart-title">תשלומים שהתקבלו לפי חודש</h3>
          {!stats.hasPayments ? (
            <div className="ndt-empty">לא נרשמו תשלומים ב-12 החודשים האחרונים</div>
          ) : (
            <div className="ndt-chart" dir="ltr">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.monthsData} margin={{ top: 24, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                    interval={1}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: "var(--color-text-light)" }}
                    tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}K` : v)}
                    width={36}
                  />
                  <Tooltip content={<ChartTooltip isMoney />} cursor={{ fill: "var(--color-background-alt)" }} />
                  <Bar dataKey="total" name="התקבל" fill={COLOR_MAIN} maxBarSize={24} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="ndt-chart-card">
          <h3 className="ndt-chart-title">קליניקות לפי מסלול</h3>
          {stats.planData.length === 0 ? (
            <div className="ndt-empty">אין נתונים להצגה</div>
          ) : (
            <div className="ndt-chart" dir="ltr">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.planData} margin={{ top: 24, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
                  <XAxis
                    dataKey="plan"
                    tickLine={false}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                    interval={0}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: "var(--color-text-light)" }}
                    width={28}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
                  <Bar dataKey="count" name="קליניקות" fill={COLOR_MAIN} maxBarSize={24} radius={[4, 4, 0, 0]}>
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{ fontSize: 11, fontWeight: 600, fill: "var(--color-text-primary)" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="ndt-chart-card">
          <h3 className="ndt-chart-title">בריאות פעילות — מתי קליניקות היו פעילות לאחרונה</h3>
          {stats.activityData.length === 0 ? (
            <div className="ndt-empty">אין נתונים להצגה</div>
          ) : (
            <div className="ndt-chart" dir="ltr">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.activityData} margin={{ top: 24, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={{ stroke: "var(--color-border)" }}
                    tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                    interval={0}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11, fill: "var(--color-text-light)" }}
                    width={28}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
                  <Bar dataKey="count" name="קליניקות" maxBarSize={24} radius={[4, 4, 0, 0]}>
                    {stats.activityData.map((entry) => (
                      <Cell key={entry.label} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="count"
                      position="top"
                      style={{ fontSize: 11, fontWeight: 600, fill: "var(--color-text-primary)" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="ndt-chart-card">
          <h3 className="ndt-chart-title">הכנסה חודשית לפי מסלול</h3>
          {stats.planRevenue.length === 0 ? (
            <div className="ndt-empty">אין לקוחות משלמים עדיין</div>
          ) : (
            <div className="ndt-hbars">
              {stats.planRevenue.map((p) => (
                <div key={p.plan} className="ndt-hbar-row">
                  <span className="ndt-hbar-label">{p.plan}</span>
                  <div className="ndt-hbar-track">
                    <div
                      className="ndt-hbar-fill"
                      style={{ width: `${Math.max(4, (p.amount / stats.planRevenueMax) * 100)}%` }}
                    />
                  </div>
                  <span className="ndt-hbar-value">{formatShekels(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="ndt-chart-card">
          <h3 className="ndt-chart-title">משלמים לפי מחזור חיוב</h3>
          {stats.cycleBreakdown.length === 0 ? (
            <div className="ndt-empty">אין לקוחות משלמים עדיין</div>
          ) : (
            <div className="ndt-hbars">
              {stats.cycleBreakdown.map((c) => (
                <div key={c.cycle} className="ndt-hbar-row">
                  <span className="ndt-hbar-label">{c.label}</span>
                  <div className="ndt-hbar-track">
                    <div
                      className="ndt-hbar-fill"
                      style={{ width: `${Math.max(4, (c.count / stats.payingCount) * 100)}%` }}
                    />
                  </div>
                  <span className="ndt-hbar-value">
                    {c.count} · {formatShekels(c.mrr)} לחודש
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default NivDataTab;
